import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../data/redux/store';
import TeacherModal from '../../../feature-module/peoples/teacher/teacherModal';
import { all_routes } from '../../../feature-module/router/all_routes';

const UserViewProfile = () => {
  // Fetch user data from the Redux store
  const user = useSelector((state: RootState) => state.user.Users);
  const userdetail = useSelector((state: RootState) => state.user.Userdetail);

  // Get the first user for display
  const currentUser = user.length > 0 ? user[0] : null;
  const roleName = currentUser?.role || userdetail?.designation || 'No role';
  const userFirstName = userdetail ? `${ userdetail.firstName || '' }`.trim() : 'User';
  const userLastName = userdetail ? ` ${ userdetail.lastName || '' }`.trim() : 'User';
  const userEmail = userdetail?.email || 'email@example.com';



  // Role -> dashboard route mapping
 const getDashboardRoute = (roleRaw: string) => {
  const role = roleRaw.toLowerCase().trim();

  const direct: Record<string, keyof typeof all_routes> = {
    teacher: "teacherDashboard",
    hm: "HeadmasterDashboard",
    admin: "adminDashboard",
    "state admin": "adminDashboard",
    "district admin": "adminDashboard",
    "block admin": "adminDashboard",
    "village admin":"adminDashboard"
  };

  if (role in direct) return all_routes[direct[role]];

  if (role.includes("teacher")) return all_routes.teacherDashboard;
  if (role.includes("head") || role.includes("hm")) return all_routes.HeadmasterDashboard;
  if (role.includes("admin")) return all_routes.adminDashboard;

  const fallback: keyof typeof all_routes = "adminDashboard";
  return all_routes[fallback] ?? "/";
};


  const dashboardLink = useMemo(() => getDashboardRoute(roleName), [roleName]);


  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Profile</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={dashboardLink}>Dashboard</Link></li>
                  <li className="breadcrumb-item active" aria-current="page">
                    User Profile
                  </li>
                </ol>
              </nav>
            </div>
            <div className="mb-2">
              <Link
                to={all_routes.editUserProfile}
                state={{ teacher: { ...currentUser, ...userdetail } }} // Pass user data
                className="btn btn-primary d-flex align-items-center"
              > <i className="ti ti-square-rounded-plus me-2" /> Edit Profile
              </Link>
            </div>
          </div>

          <div className="row">
            <div className="col-xxl-12 col-xl-8">
              <div className="row">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header">
                      <h5>Basic Information</h5>
                    </div>
                    <div className="card-body">
                      <div className="border rounded p-3 pb-0">
                        <div className="row">
                          <div className="col-sm-6 col-lg-4">
                            <div className="mb-3">
                              <p className="text-dark fw-medium mb-1">First Name</p>
                              <p>{userFirstName || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="col-sm-6 col-lg-4">
                            <div className="mb-3">
                              <p className="text-dark fw-medium mb-1">Last Name</p>
                              <p>{userLastName || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="col-sm-6 col-lg-4">
                            <div className="mb-3">
                              <p className="text-dark fw-medium mb-1">Email</p>
                              <p>{userEmail || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="col-sm-6 col-lg-4">
                            <div className="mb-3">
                              <p className="text-dark fw-medium mb-1">Phone Number</p>
                              <p>{userdetail?.phone || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="col-sm-6 col-lg-4">
                            <div className="mb-3">
                              <p className="text-dark fw-medium mb-1">Aadhar Number</p>
                              <p>{userdetail?.aadharNumber || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="col-sm-6 col-lg-4">
                            <div className="mb-3">
                              <p className="text-dark fw-medium mb-1">Designation</p>
                              <p>{userdetail?.designation || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xxl-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-header">
                      <h5>Role Information</h5>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <p className="text-dark fw-medium mb-1">Assigned Roles</p>
                        <div>
                          <span className="badge bg-primary me-1 mb-1">
                            {roleName}
                          </span>
                        </div>
                      </div>
                      <div className="mb-3">
                        <p className="text-dark fw-medium mb-1">Teacher ID</p>
                        <p>{userdetail?.id ?? 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xxl-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-header">
                      <h5>Contact Information</h5>
                    </div>
                    <div className="card-body">
                      <div className="d-flex align-items-center mb-3">
                        <span className="avatar avatar-md bg-light-300 rounded me-2 flex-shrink-0 text-default">
                          <i className="ti ti-mail" />
                        </span>
                        <div>
                          <p className="text-dark fw-medium mb-1">Email Address</p>
                          <p>{userEmail ?? 'N/A'}</p>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="avatar avatar-md bg-light-300 rounded me-2 flex-shrink-0 text-default">
                          <i className="ti ti-phone" />
                        </span>
                        <div>
                          <p className="text-dark fw-medium mb-1">Phone Number</p>
                          <p>{userdetail?.phone ?? 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TeacherModal />
    </>
  );
};

export default UserViewProfile;